import React, {useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Chip, Stack, CircularProgress, Alert, Divider
} from '@mui/material';
import {
  HomeOutlined, TrendingUpOutlined, MonitorHeartOutlined, MapOutlined,
  AssignmentOutlined, ReportProblemOutlined, PointOfSaleOutlined, AnalyticsOutlined
} from '@mui/icons-material';
import { ROLE_META, ROLES, getQuickLinks } from '../config/roleConfig';
import { getRabForSupplier } from './API/bantuan';
import { getSupplierBarang } from './API/supplierPenyedia';

const ICON_MAP = {
  Dashboard: <HomeOutlined fontSize="large" />,
  'Proses Bantuan': <TrendingUpOutlined fontSize="large" />,
  'Monitoring Bantuan': <MonitorHeartOutlined fontSize="large" />,
  'Peta Sebaran': <MapOutlined fontSize="large" />,
  'Data Bantuan': <AssignmentOutlined fontSize="large" />,
  'Keluhan': <ReportProblemOutlined fontSize="large" />,
  'Input Pembelian (POS)': <PointOfSaleOutlined fontSize="large" />,
  'Rekap & Anggaran': <AnalyticsOutlined fontSize="large" />,
  'Realisasi Anggaran': <AnalyticsOutlined fontSize="large" />,
};

const RoleHomeDashboard = ({ roleKey }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authAdmin);
  const role = roleKey || user?.role;
  const meta = ROLE_META[role] || { label: role, description: '', color: '#0A5EB0' };
  const links = getQuickLinks(role);
  const [isMobile, setIsMobile] = useState(false);
  const [supplierSummary, setSupplierSummary] = useState(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierError, setSupplierError] = useState('');
useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (role !== ROLES.SUPPLIER) return;

    let active = true;
    const loadSupplierSummary = async () => {
      setSupplierLoading(true);
      setSupplierError('');
      try {
        const [rabResponse, supplierResponse] = await Promise.all([
          getRabForSupplier(),
          getSupplierBarang({ page: 1, limit: 100 })
        ]);
        if (!active) return;

        const rabRows = rabResponse.data?.data || [];
        const supplierRows = supplierResponse.data?.data || [];
        const bantuanIds = new Set([
          ...rabRows.map((rab) => rab.BantuanId).filter(Boolean),
          ...supplierRows.flatMap((supplier) => supplier.linkedBantuanIds || [])
        ]);
        const jumlahPembelian = supplierRows.reduce((sum, supplier) => sum + Number(supplier.jumlahPembelian || 0), 0);
        const totalPembelian = supplierRows.reduce((sum, supplier) => sum + Number(supplier.totalTransaksiKeseluruhan || 0), 0);

        setSupplierSummary({
          jumlahBantuan: bantuanIds.size,
          jumlahRab: rabRows.length,
          jumlahPembelian,
          totalPembelian,
          rabTerbaru: rabRows.slice(0, 5),
          pembelianTerbaru: supplierRows
            .flatMap((supplier) => (supplier.Pembelian || []).map((pembelian) => ({ ...pembelian, namaSupplier: supplier.namaSupplier })))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5)
        });
      } catch (error) {
        if (active) setSupplierError(error.response?.data?.message || 'Gagal memuat data supplier');
      } finally {
        if (active) setSupplierLoading(false);
      }
    };

    loadSupplierSummary();
    return () => { active = false; };
  }, [role]);
 
  const contentStyle = isMobile 
    ? {} // No margins on mobile
    : { marginLeft: "250px", marginRight: "20px" };
    
  return (
      <div className="data-content-wrapper">
          <div className="main-content" style={contentStyle}>
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Chip
          label={meta.label}
          sx={{ alignSelf: 'flex-start', bgcolor: meta.color, color: '#fff', fontWeight: 600 }}
        />
        <Typography variant="h5" fontWeight={700}>
          Selamat datang, {user?.username || 'Pengguna'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {meta.description}
        </Typography>
      </Stack>

      {role === ROLES.SUPPLIER && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Ringkasan RAB dan Pembelian</Typography>
          {supplierLoading && <CircularProgress size={28} />}
          {supplierError && <Alert severity="error" sx={{ mb: 2 }}>{supplierError}</Alert>}
          {supplierSummary && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  ['Penerima Terkait', supplierSummary.jumlahBantuan],
                  ['Data RAB', supplierSummary.jumlahRab],
                  ['Data Pembelian', supplierSummary.jumlahPembelian],
                  ['Total Pembelian', `Rp ${supplierSummary.totalPembelian.toLocaleString('id-ID')}`]
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card elevation={1}><CardContent>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="h6" fontWeight={700}>{value}</Typography>
                    </CardContent></Card>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card elevation={1}><CardContent>
                    <Typography variant="subtitle1" fontWeight={700}>RAB Penerima Bantuan</Typography>
                    <Divider sx={{ my: 1 }} />
                    {supplierSummary.rabTerbaru.length === 0 ? <Typography variant="body2" color="text.secondary">Belum ada RAB terkait.</Typography> : supplierSummary.rabTerbaru.map((rab) => (
                      <Box key={rab.id} sx={{ py: 1 }}>
                        <Typography variant="body2" fontWeight={600}>#{rab.BantuanId} — {rab.Bantuan?.pemohon?.nama || rab.Bantuan?.namaLengkap || rab.Bantuan?.JenisBantuan || 'Penerima bantuan'}</Typography>
                        <Typography variant="caption" color="text.secondary">Status: {rab.status || '-'} · Total RAB: Rp {Number(rab.totalHarga || 0).toLocaleString('id-ID')}</Typography>
                      </Box>
                    ))}
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card elevation={1}><CardContent>
                    <Typography variant="subtitle1" fontWeight={700}>Pembelian Supplier</Typography>
                    <Divider sx={{ my: 1 }} />
                    {supplierSummary.pembelianTerbaru.length === 0 ? <Typography variant="body2" color="text.secondary">Belum ada pembelian terkait.</Typography> : supplierSummary.pembelianTerbaru.map((pembelian) => (
                      <Box key={pembelian.id} sx={{ py: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Bantuan #{pembelian.BantuanId || '-'} — {pembelian.namaSupplier || 'Supplier'}</Typography>
                        <Typography variant="caption" color="text.secondary">Total: Rp {Number(pembelian.totalTransaksi || 0).toLocaleString('id-ID')}</Typography>
                      </Box>
                    ))}
                  </CardContent></Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}

      <Grid container spacing={2}>
        {links.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.to}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardActionArea onClick={() => navigate(item.to)} sx={{ height: '100%', p: 2 }}>
                <CardContent>
                  <Box sx={{ color: meta.color, mb: 1.5 }}>
                    {ICON_MAP[item.text] || <AssignmentOutlined fontSize="large" />}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {item.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Klik untuk membuka
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {links.length === 0 && (
        <Typography color="text.secondary">
          Gunakan menu sidebar untuk navigasi.
        </Typography>
      )}
    </Box>
    </div>
    </div>
  );
};

export default RoleHomeDashboard;
